import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { KeyRound, Shield, Radio, Target } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const TIERS = {
  REGULAR: 10, VIP2: 15, VIP3: 20, PRO1: 25, PRO2: 30, PRO3: 35,
};

export default function Settings() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["settings"], queryFn: api.getSettings });
  const { data: meta } = useQuery({ queryKey: ["meta"], queryFn: api.meta });
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (data) setForm({
      ...data,
      default_bonus_rate: (data.default_bonus_rate ?? 0.1) * 100,
      default_darkex_fee_rate: (data.default_darkex_fee_rate ?? 0.0005) * 100,
      default_counter_fee_rate: (data.default_counter_fee_rate ?? 0.0005) * 100,
      spread_alert_threshold_pct: data.spread_alert_threshold_pct ?? 0.1,
    });
  }, [data]);

  const mutation = useMutation({
    mutationFn: api.updateSettings,
    onSuccess: () => {
      toast.success("Ayarlar kaydedildi");
      qc.invalidateQueries({ queryKey: ["settings"] });
    },
    onError: (e) => toast.error(errMsg(e, "Kayıt başarısız")),
  });

  if (!form) return <div className="p-8 text-zinc-500">Yükleniyor…</div>;

  const update = (k, v) => setForm({ ...form, [k]: v });

  const save = () => {
    const payload = {
      ...form,
      default_bonus_rate: parseFloat(form.default_bonus_rate) / 100,
      default_darkex_fee_rate: parseFloat(form.default_darkex_fee_rate) / 100,
      default_counter_fee_rate: parseFloat(form.default_counter_fee_rate) / 100,
      default_leverage: parseFloat(form.default_leverage),
      default_position_size_usd: parseFloat(form.default_position_size_usd),
      spread_alert_threshold_pct: parseFloat(form.spread_alert_threshold_pct),
      signal_score_threshold: parseInt(form.signal_score_threshold, 10),
      signal_poll_seconds: parseInt(form.signal_poll_seconds, 10),
      // Smart Entry alanları → sayısal tip
      smart_entry_max_slippage_bps: parseFloat(
        form.smart_entry_max_slippage_bps ?? 5,
      ),
      smart_entry_inter_chunk_delay_ms: parseInt(
        form.smart_entry_inter_chunk_delay_ms ?? 100, 10,
      ),
      smart_entry_max_chunks: parseInt(
        form.smart_entry_max_chunks ?? 8, 10,
      ),
    };
    mutation.mutate(payload);
  };

  return (
    <div className="p-5 space-y-4 max-w-4xl">
      <Panel title="Darkex Tier · Kayıp-Kurtarma Seviyesi" subtitle="Tier seçimi bonus oranını otomatik belirler. Tier'i kendin gir — Darkex API'de tier sorgulama endpoint'i yok." icon={Target}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field label="Tier (manuel)">
            <Select value={form.tier} onValueChange={(v) => update("tier", v)}>
              <SelectTrigger data-testid="set-tier" className="bg-transparent border-[#27272A] h-9 rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#27272A] rounded-sm text-white">
                {Object.entries(TIERS).map(([t, pct]) => (
                  <SelectItem key={t} value={t}>{t} (%{pct})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Bonus Oranı (Tier'den türetilir)">
            <div
              data-testid="bonus-display"
              className="bg-transparent border border-[#27272A] h-9 rounded-sm px-3 flex items-center"
            >
              <span className="text-sm font-mono font-semibold text-[#ff5d15]">
                %{Math.round(Number(form.default_bonus_rate || 0) * 100) || TIERS[form.tier] || 10}
              </span>
              <span className="text-[10px] text-zinc-500 ml-2">(kaydetince güncellenir)</span>
            </div>
          </Field>
          <Field label="İşlem Sembolü">
            <Select value={form.signal_symbol} onValueChange={(v) => update("signal_symbol", v)}>
              <SelectTrigger data-testid="set-symbol" className="bg-transparent border-[#27272A] h-9 rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#27272A] rounded-sm text-white">
                {(meta?.pairs || ["ETHUSDT"]).map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <div className="mt-3 flex items-start gap-2 text-[11px] text-amber-500/80 bg-amber-500/[0.04] border border-amber-500/20 rounded-sm p-2">
          <Shield className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>
            <b className="text-amber-400">Önemli — honor system:</b> Darkex'in resmi tier sorgulama API'si
            yok. Gerçek tier'ını <b>kendin</b> Darkex hesabından kontrol et ve
            buraya doğru gir. <b className="text-amber-400">Yanlış tier =
            yanlış bonus projeksiyonu</b> — gerçekte alacağın bonus Darkex'in
            karar verdiği orandır, bizim hesabımız değil.
          </span>
        </div>
      </Panel>

      <Panel title="İşlem Varsayılanları">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field label="Tercih Karşı Borsa">
            <Select value={form.preferred_counter_exchange} onValueChange={(v) => update("preferred_counter_exchange", v)}>
              <SelectTrigger data-testid="set-preferred-exchange" className="bg-transparent border-[#27272A] h-9 rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#27272A] rounded-sm text-white">
                <SelectItem value="OKX">OKX</SelectItem>
                <SelectItem value="BINANCE">Binance</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Varsayılan Kaldıraç (x)">
            <NumberInput testId="set-leverage" value={form.default_leverage} onChange={(v) => update("default_leverage", v)} step="0.5" />
          </Field>
          <Field label="Pozisyon Boyutu ($) · sinyalle">
            <NumberInput testId="set-size-usd" value={form.default_position_size_usd} onChange={(v) => update("default_position_size_usd", v)} step="50" />
          </Field>
          <Field label="Darkex Komisyon (%)">
            <NumberInput testId="set-darkex-fee" value={form.default_darkex_fee_rate} onChange={(v) => update("default_darkex_fee_rate", v)} step="0.001" />
          </Field>
          <Field label="Karşı Borsa Komisyon (%)">
            <NumberInput testId="set-counter-fee" value={form.default_counter_fee_rate} onChange={(v) => update("default_counter_fee_rate", v)} step="0.001" />
          </Field>
          <Field label="Min Pozisyon ($)">
            <NumberInput testId="set-min-pos" value={form.min_position_size_usd} onChange={(v) => update("min_position_size_usd", v)} step="10" />
          </Field>
          <Field label="Max Pozisyon ($)">
            <NumberInput testId="set-max-pos" value={form.max_position_size_usd} onChange={(v) => update("max_position_size_usd", v)} step="100" />
          </Field>
          <Field label="🛑 Günlük Zarar Kesici ($) · 0=kapalı">
            <NumberInput testId="set-daily-loss-limit" value={form.daily_loss_limit_usd} onChange={(v) => update("daily_loss_limit_usd", v)} step="50" />
          </Field>
          <Field label="Fark Alarm Eşiği (%)">
            <NumberInput testId="set-threshold" value={form.spread_alert_threshold_pct} onChange={(v) => update("spread_alert_threshold_pct", v)} step="0.01" />
          </Field>
          <Field label="Telegram Özet Eşiği (bildirim)">
            <NumberInput testId="set-digest-threshold" value={form.telegram_digest_threshold} onChange={(v) => update("telegram_digest_threshold", v)} step="1" />
          </Field>
        </div>
      </Panel>

      <Panel title="🧩 Smart Entry · Akıllı Emir Bölme" subtitle="Büyük emirleri book derinliğine göre slippage-aware parçalara böler. 1 aylık canlı kullanımda agresif piyasalarda %5-30 erimeyi engeller." icon={Target}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Durum">
            <div className="flex items-center gap-2 h-9 px-3 bg-transparent border border-[#27272A] rounded-sm">
              <Switch
                data-testid="set-smart-entry-enabled"
                checked={form.smart_entry_enabled !== false}
                onCheckedChange={(v) => update("smart_entry_enabled", v)}
              />
              <span className="text-sm">
                {form.smart_entry_enabled !== false ? "Açık" : "Kapalı"}
              </span>
            </div>
          </Field>
          <Field label="Max Slippage (bps)">
            <NumberInput
              testId="set-smart-entry-max-slip-bps"
              value={form.smart_entry_max_slippage_bps ?? 5}
              onChange={(v) => update("smart_entry_max_slippage_bps", v)}
              step="0.5"
            />
          </Field>
          <Field label="Chunk Gecikme (ms)">
            <NumberInput
              testId="set-smart-entry-delay-ms"
              value={form.smart_entry_inter_chunk_delay_ms ?? 100}
              onChange={(v) => update("smart_entry_inter_chunk_delay_ms", v)}
              step="10"
            />
          </Field>
          <Field label="Max Chunk Sayısı">
            <NumberInput
              testId="set-smart-entry-max-chunks"
              value={form.smart_entry_max_chunks ?? 8}
              onChange={(v) => update("smart_entry_max_chunks", v)}
              step="1"
            />
          </Field>
        </div>
        <div className="mt-3 text-[11px] text-zinc-500 bg-transparent border border-[#27272A] rounded-sm p-2 leading-relaxed">
          <Shield className="w-3 h-3 inline mr-1 text-[#ff5d15]" />
          <b>Önerilen değerler:</b> Sakin piyasa → <code className="text-zinc-300">3-5 bps</code>;
          orta volatilite → <code className="text-zinc-300">5-10 bps</code>;
          yüksek volatilite (ETF/FOMC, breakout) → <code className="text-zinc-300">10-20 bps</code>.
          Chunk gecikmesi book'un toparlanmasına izin verir; 80-150ms ideal.
          Kapatırsan eski tek-atış davranışına döner.
        </div>
      </Panel>

      <Panel title="Sinyal Motoru" subtitle="CoinGecko + TradingView puan eşiği üstünde Telegram'a sinyal yollanır." icon={Radio}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field label="Skor Eşiği (0-100)">
            <NumberInput testId="set-signal-threshold" value={form.signal_score_threshold} onChange={(v) => update("signal_score_threshold", v)} step="1" />
          </Field>
          <Field label="Tarama Aralığı (sn)">
            <NumberInput testId="set-poll-seconds" value={form.signal_poll_seconds} onChange={(v) => update("signal_poll_seconds", v)} step="10" />
          </Field>
          <Field label="Telegram Bildirimi">
            <div className="flex items-center gap-2 h-9 px-3 bg-transparent border border-[#27272A] rounded-sm">
              <Switch
                data-testid="set-telegram-enabled"
                checked={!!form.telegram_enabled}
                onCheckedChange={(v) => update("telegram_enabled", v)}
              />
              <span className="text-sm">{form.telegram_enabled ? "Açık" : "Kapalı"}</span>
            </div>
          </Field>
        </div>
        <div className="mt-3 text-[11px] text-zinc-500 bg-transparent border border-[#27272A] rounded-sm p-2">
          <Shield className="w-3 h-3 inline mr-1 text-[#ff5d15]" />
          Bot token'ı <code className="text-zinc-300">/app/backend/.env</code> içindeki{" "}
          <code className="text-zinc-300">TELEGRAM_BOT_TOKEN</code> değişkenine eklenmelidir. Chat ID zaten ayarlı: <code>6725094245</code>.
        </div>
      </Panel>

      <ExchangePanel
        title="Darkex API"
        subtitle="Loss-recovery kampanyası ve canlı emir gönderimi için."
        accent="#ff5d15"
        fields={[
          { label: "API Key", val: form.darkex_api_key, key: "darkex_api_key", testId: "set-darkex-key" },
          { label: "API Secret", val: form.darkex_api_secret, key: "darkex_api_secret", testId: "set-darkex-secret", secret: true },
        ]}
        update={update}
      />

      <ExchangePanel
        title="OKX API"
        subtitle="Karşı bacaktaki (Kâr Bacağı) pozisyonlar için alternatif."
        accent="#FFFFFF"
        fields={[
          { label: "API Key", val: form.okx_api_key, key: "okx_api_key", testId: "set-okx-key" },
          { label: "API Secret", val: form.okx_api_secret, key: "okx_api_secret", testId: "set-okx-secret", secret: true },
          { label: "Passphrase", val: form.okx_passphrase, key: "okx_passphrase", testId: "set-okx-passphrase", secret: true },
        ]}
        update={update}
      />

      <ExchangePanel
        title="Binance API"
        subtitle="Kâr bacağı (Darkex'in karşısı). Futures izinli, IP whitelist boş veya proxy IP'si olmalı. Trafik UK proxy üzerinden gider."
        accent="#F0B90B"
        fields={[
          { label: "API Key", val: form.binance_api_key, key: "binance_api_key", testId: "set-binance-key" },
          { label: "API Secret", val: form.binance_api_secret, key: "binance_api_secret", testId: "set-binance-secret", secret: true },
        ]}
        update={update}
      />

      <div className="flex justify-end">
        <Button data-testid="save-settings-btn" onClick={save} disabled={mutation.isPending} className="bg-[#ff5d15] hover:bg-[#f0b90b] text-black rounded-sm h-9 px-5 font-semibold">
          {mutation.isPending ? "Kaydediliyor…" : "Ayarları Kaydet"}
        </Button>
      </div>
    </div>
  );
}

const Panel = ({ title, subtitle, children, icon: Icon }) => (
  <div className="bg-[#121212] border border-[#27272A] rounded-md">
    <div className="px-4 py-3 border-b border-[#1A1A1A]">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-[#ff5d15]" />}
        <h3 className="text-sm font-semibold tracking-tight">{title}</h3>
      </div>
      {subtitle && <p className="text-xs text-zinc-500 mt-0.5">{subtitle}</p>}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const Grid = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">{children}</div>
);

const Field = ({ label, children }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">{label}</Label>
    {children}
  </div>
);

const NumberInput = ({ value, onChange, step, testId }) => (
  <Input data-testid={testId} type="number" step={step} value={value} onChange={(e) => onChange(e.target.value)} className="bg-transparent border-[#27272A] h-9 rounded-sm font-mono" />
);

const KeyField = ({ label, value, onChange, secret, testId }) => {
  const isMasked = typeof value === "string" && value.startsWith("\u2022\u2022\u2022\u2022");
  return (
    <Field label={label}>
      <Input
        data-testid={testId}
        type={secret && !isMasked ? "password" : "text"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Tanımlı değil"
        className="bg-transparent border-[#27272A] h-9 rounded-sm font-mono text-xs"
        title={isMasked ? "Mevcut anahtar şifreli. Değiştirmek için temizleyip yeni değer gir." : undefined}
      />
      {isMasked && (
        <p className="text-[10px] text-zinc-500 mt-1">
          🔒 Şifreli saklandı · değiştirmek için temizle ve yeniden gir
        </p>
      )}
    </Field>
  );
};

const ExchangePanel = ({ title, subtitle, accent, fields, update }) => (
  <div className="bg-[#121212] border border-[#27272A] rounded-md">
    <div className="px-4 py-3 border-b border-[#1A1A1A] flex items-center justify-between">
      <div>
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-4 rounded-sm" style={{ background: accent }} />
          <h3 className="text-sm font-semibold tracking-tight">{title}</h3>
        </div>
        {subtitle && <p className="text-xs text-zinc-500 mt-0.5 ml-3.5">{subtitle}</p>}
      </div>
      <KeyRound className="w-4 h-4 text-zinc-600" />
    </div>
    <div className="p-4">
      <Grid>
        {fields.map((f) => (
          <KeyField
            key={f.key}
            label={f.label}
            value={f.val || ""}
            onChange={(v) => update(f.key, v)}
            testId={f.testId}
            secret={!!f.secret}
          />
        ))}
      </Grid>
    </div>
  </div>
);
